import { useState } from "react";
import { t } from "i18next";
import { HiOutlinePlus } from "react-icons/hi2";
import { TbLoader2, TbScreenshot } from "react-icons/tb";
import { Button, Dropdown, Notification, toast } from "components/ui";
import { PLACEMENT } from "components/ui/utils/constant";
import MediaPreview from "components/posts_previews/components/MediaPreview";
import AddAssetButton from "components/posts_previews/components/AddAssetButton";
import ThumbnailPicker from "components/posts_previews/components/ThumbnailPicker";
import ComposerField from "./ComposerField";

/**
 * Media section of the composer form: a thumbnail grid (delete / AI-edit /
 * fullscreen per tile), an "add" tile backed by the existing AddAssetButton
 * menu (AI image & video, stock, GIF, library, reuse from other networks),
 * and — when the network supports it — the video cover/thumbnail picker.
 */
const ComposerMediaField = ({
    post,
    network,
    isEditable = true,
    allPosts = [],
    constants = undefined,
    onAssetNew,
    onAssetDelete,
    onAssetEdit,
    onPreferencesEdit,
    onCoverEdit
}) => {

    const [preventCoverClose, setPreventCoverClose] = useState(false);

    const mediaUrls = constants?.media_urls;
    const maxMedia = mediaUrls?.max_length
        || ((mediaUrls?.max_images_length || 0) + (mediaUrls?.max_videos_length || 0) + (mediaUrls?.max_gifs_length || 0))
        || undefined;

    // Cover/thumbnail picking mirrors MoreOptions: only for a single leading
    // video, on networks whose constants allow a frame or file cover.
    // Instagram additionally restricts it to single-video FEED posts.
    const hasLeadingVideo = !!post?.assets?.[0]?.video?.url;
    const allowsCover = !!mediaUrls?.allow_frame_cover || !!mediaUrls?.allow_file_cover;
    const instagramAllowsCover = network !== "instagram" || (
        post?.preferences?.media_product_type?.toLowerCase() == "feed" && post?.assets?.length === 1
    );
    const showCover = isEditable && hasLeadingVideo && allowsCover && instagramAllowsCover;
    const hasCover = !!post?.cover?.url || !!post?.preferences?.video_cover_timestamp_ms;

    const handleCoverTimestampSelect = (timestamp) => {
        onPreferencesEdit(post.postIndex, { ...(post?.preferences || {}), video_cover_timestamp_ms: timestamp });
    };

    const handleCoverImageSelect = (url) => {
        onCoverEdit(post.postIndex, { url });
        toast.push(<Notification title={url ? `Cover image selected` : `Cover image removed`} type="success" />);
    };

    return (
        <ComposerField
            label={t('composer.mediaLabel', 'Media')}
            counter={maxMedia
                ? <span className="text-xs tabular-nums text-tertiary-400 dark:text-gray-500">
                    {post?.assets?.length || 0} / {maxMedia}
                </span>
                : undefined
            }
        >
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                {post?.assets?.map((asset, assetIndex) => (
                    <div
                        key={asset?.id || asset?.image?.url || asset?.video?.url || assetIndex}
                        className="relative aspect-square rounded-lg overflow-hidden ring-1 ring-gray-200 dark:ring-gray-700 bg-gray-100 dark:bg-gray-900"
                    >
                        <MediaPreview
                            asset={asset}
                            network={network}
                            isEditable={isEditable}
                            videoControls={false}
                            parentClassName="!w-full !h-full"
                            className="!w-full !h-full object-cover"
                            onAssetDelete={() => onAssetDelete(post.postIndex, assetIndex)}
                            onAssetEdit={(src) => onAssetEdit(post.postIndex, assetIndex, src)}
                        />
                    </div>
                ))}
                {isEditable && (
                    <AddAssetButton
                        network={network}
                        post={post}
                        allPosts={allPosts}
                        placement={PLACEMENT.BOTTOM_START}
                        portal={true}
                        onAssetNew={(asset) => onAssetNew(post.postIndex, asset)}
                        onAssetDelete={(assetIndex) => onAssetDelete(post.postIndex, assetIndex)}
                        renderTitle={({ isGenerating, canAddMore } = {}) => (
                            isGenerating
                                ? <div className="aspect-square w-full rounded-lg bg-gradient-ai text-white flex flex-col items-center justify-center gap-1.5 cursor-wait shadow-lg">
                                    <TbLoader2 className="text-2xl animate-spin" />
                                    <span className="text-[11px] font-semibold animate-pulse">
                                        {t('composer.generating', 'Generating...')}
                                    </span>
                                </div>
                                : <div className={`aspect-square w-full rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 text-tertiary-400 dark:text-gray-500 transition-colors flex flex-col items-center justify-center gap-1 ${canAddMore === false
                                    ? `opacity-40 cursor-not-allowed`
                                    : `hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-300 hover:bg-primary-50/50 dark:hover:bg-primary-500/5 cursor-pointer`
                                }`}>
                                    <HiOutlinePlus className="text-xl" />
                                    <span className="text-[11px] font-medium">{t('composer.addMedia', 'Add')}</span>
                                </div>
                        )}
                    />
                )}
            </div>
            {showCover && (
                <div className="mt-3 flex">
                    <Dropdown
                        preventClose={preventCoverClose}
                        placement={PLACEMENT.BOTTOM_START}
                        parentClassName="!shrink-0"
                        menuClass="w-auto p-0 overflow-y-auto max-h-[min(70vh,640px)]"
                        portal={true}
                        renderTitle={
                            <Button
                                size="xs"
                                variant="twoTone"
                                color="primary-600"
                                className="!rounded-lg"
                                icon={<TbScreenshot />}
                            >
                                {hasCover
                                    ? t('composer.changeCover', 'Change video cover...')
                                    : t('composer.selectCover', 'Select video cover...')
                                }
                            </Button>
                        }
                    >
                        <ThumbnailPicker
                            post={post}
                            onSelectVideoCoverTimestamp={handleCoverTimestampSelect}
                            onSelectCoverImage={handleCoverImageSelect}
                            showFrameTab={!!mediaUrls?.allow_frame_cover}
                            showFileTab={!!mediaUrls?.allow_file_cover}
                            setPreventClose={setPreventCoverClose}
                        />
                    </Dropdown>
                </div>
            )}
        </ComposerField>
    );
};

export default ComposerMediaField;
